<template>
  <main class="cashbox-list">
    <div class="cashbox-list__body">
      <div class="cashbox-list__header">
        <h1 class="cashbox-list__title">Кассы</h1>
        <button
          class="cashbox-list__add"
          type="button"
          v-on:click="onCreateCashbox"
        >Добавить кассу</button>
      </div>
      <div class="cashbox-list__table">
        <div class="cashbox-list__table__header">
          <div class="cashbox-list__table__header__cell">По умолчанию</div>
          <div class="cashbox-list__table__header__cell">Кассы</div>
        </div>
        <button
          class="cashbox-list__table__row"
          type="button"
          v-for="cashbox in cashboxes"
          v-bind:key="cashbox.id"
          v-on:click="onEditCashbox(cashbox)"
        >
          <div data-label="По умолчанию" class="cashbox-list__table__cell">
            <button
              class="cashbox-list__input-radio"
              type="button"
              v-bind:disabled="isAwaitRequest"
              v-bind:class="cashbox.tags"
              v-on:click.stop="onSelectDefaultCashbox(cashbox.id)"
            />
          </div>
          <div data-label="Кассы" class="cashbox-list__table__cell name">{{ cashbox.name }}</div>
          <template v-if="!cashbox.isMain">
            <div data-label="Редактировать" class="cashbox-list__table__cell action">
              <button
                type="button"
                class="cashbox-list__button-edit"
                title="Редактировать кассу"
                v-on:click="onEditCashbox(cashbox)"
              />
            </div>
            <div data-label="Удалить" class="cashbox-list__table__cell action">
              <button
                type="button"
                class="cashbox-list__button-delete"
                title="Удалить кассу"
                v-on:click.stop="onDeleteCashbox(cashbox)"
              />
            </div>
          </template>
        </button>
      </div>
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-if="selectedCashbox"
        v-bind:label="formLabel"
        v-bind:need-confirm="needConfirmForm"
        v-on:cancel="onCloseForm"
      >
        <form class="cashbox-list__form" @submit.prevent="onSubmit">
          <input-text
            placeholder="Например, Основная касса"
            title="Название кассы"
            required
            v-letter-capital
            v-bind:disabled="isNameInputDisabled"
            v-bind:violations="nameInputErrors"
            v-model="cashboxName"
          />
          <div class="cashbox-list__form__actions">
            <button
              class="cashbox-list__form__cancel"
              type="button"
              v-on:click="onCloseForm">Отмена</button>
            <button
              class="cashbox-list__form__submit"
              type="submit"
              v-bind:disabled="isAwaitRequest">Сохранить</button>
          </div>
        </form>
      </confirm-side-dialog>
    </side-slide-transition>
    <modal-dialog
      with-cross
      v-bind:value="shownCashOutDialog"
      v-on:input="onCloseCashOutDialog"
      title="Удаление кассы"
    >
      <span class="v-card__text__description">Для удаления кассы произведите изъятие денежных средств</span>
      <router-link
        class="cashbox-list__button-cash-out"
        type="button"
        v-bind:to="{ name: 'ClinicPanelCashbox', params: { initialCashboxId: this.cashboxIdForCashOut } }"
      >Изъять</router-link>
    </modal-dialog>
  </main>
</template>

<script>
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import InputText from '@/components/InputText.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

export default {
  components: {
    ConfirmSideDialog,
    ModalDialog,
    InputText,
    SideSlideTransition,
  },
  created() {
    this.reloadCashboxList().finally(() => {
      this.isAwaitLoading = false;
    });
  },
  computed: {
    nameInputErrors: resolveInputErrors('name'),
    cashboxes() {
      return this.cashboxList.map((cashbox) => ({
        id: cashbox.id,
        isMain: cashbox.tags.includes('main'),
        tags: cashbox.tags,
        name: cashbox.name,
      }));
    },
    formLabel() {
      return this.selectedCashbox && this.selectedCashbox.id
        ? 'Редактирование кассы'
        : 'Создание кассы';
    },
    needConfirmForm() {
      if (!this.selectedCashbox) {
        return false;
      }
      if (!this.selectedCashbox.id) {
        return this.cashboxName !== '';
      }
      return this.cashboxName !== this.selectedCashbox.name;
    },
    isNameInputDisabled() {
      return this.selectedCashbox && this.selectedCashbox.tags.includes('main');
    },
    shownCashOutDialog() {
      return this.cashboxIdForCashOut !== null;
    },
  },
  data() {
    return {
      cashboxList: [],
      isAwaitLoading: true,
      isAwaitRequest: false,
      inputErrors: {},
      selectedCashbox: null,
      cashboxName: '',
      defaultCashboxId: null,
      cashboxIdForCashOut: null,
    };
  },
  methods: {
    reloadCashboxList() {
      const onSuccess = (list) => {
        this.cashboxList = list;
      };
      return this.$cashboxGateway
        .viewListForSettings()
        .then((result) => result.fold(this.notifyFail, onSuccess));
    },
    onCreateCashbox() {
      this.inputErrors = {};
      this.selectedCashbox = {
        name: '',
        tags: [],
      };
      this.cashboxName = '';
      this.cashboxPaymentMode = null;
    },
    onDeleteCashbox(cashbox) {
      this
        .showAcceptDialog('История операций по кассе будет удалена безвозвратно! Продолжить?')
        .then(({ result }) => {
          if (result !== DialogResult.YES) {
            return;
          }
          this.deleteCashbox(cashbox.id);
        });
    },
    onEditCashbox(cashbox) {
      if (cashbox.isMain) { return; }
      this.inputErrors = {};
      this.selectedCashbox = cashbox;
      this.cashboxName = cashbox.name;
    },
    onCloseForm() {
      this.selectedCashbox = null;
    },
    createCashbox(request) {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.onCloseForm();
        this.reloadCashboxList();
      };
      this.$cashboxGateway
        .createCashbox(request)
        .then((result) => {
          result.fold(onFail, onSuccess);
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    showCashOutDialog(cashboxId) {
      this.cashboxIdForCashOut = cashboxId;
    },
    deleteCashbox(cashboxId) {
      const onFail = (error) => {
        if (error.has('balance')) {
          this.showCashOutDialog(cashboxId);
        }
        this.notifyFail(error.getTitle());
      };
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.reloadCashboxList();
      };
      this.$cashboxGateway.deleteCashbox(cashboxId).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    editCashbox(cashboxId, request) {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.onCloseForm();
        this.reloadCashboxList();
      };
      this.$cashboxGateway
        .editCashbox(cashboxId, request)
        .then((result) => {
          result.fold(onFail, onSuccess);
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    onSubmit() {
      const request = { name: this.cashboxName };
      this.isAwaitRequest = true;
      if (this.selectedCashbox.id) {
        this.editCashbox(this.selectedCashbox.id, request);
      } else {
        this.createCashbox(request);
      }
    },
    onSelectDefaultCashbox(cashboxId) {
      this.isAwaitLoading = true;
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.reloadCashboxList();
      };
      this.$cashboxGateway
        .setDefaultCashbox(cashboxId)
        .then((result) => result.fold(this.notifyFail, onSuccess))
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    onCloseCashOutDialog() {
      this.cashboxIdForCashOut = null;
    },
  },
  mixins: [builtInDialogMixin, notifierMixin],
  name: 'ClinicPanelCashboxList',
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.cashbox-list {
  width: 100%;

  &__body {
    padding: 38px 20px 48px 23px;
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    gap: 15px 10px;
    justify-content: space-between;
  }

  &__add {
    @include buttons.btn-blue(250px, 48px, 16px);
    @include buttons.btn-blue-add(19px);
    min-width: 215px;
  }

  &__table {
    margin: 25px 0 0 0;
    font-size: 14px;
    line-height: 16px;
    width: 100%;

    &__header, &__row  {
      display: grid;
      grid-template-columns: 150px 1fr 40px 40px;
      align-items: center;
    }

    &__header {
      border-bottom: 2px solid #0489E0;
      font-weight: 700;

      &__cell {
        padding: 10px 8px;
        text-align: left;
      }
    }

    &__cell {
      padding: 15px 8px;
      text-align: left;
      white-space: nowrap;
      text-transform: none;

      &.action {
        justify-self: center;
      }

      &.name {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__row {
      width: 100%;
      border-bottom: 1px solid #EAEFF3;

      &:hover {
        background: var(--fill-color);
      }
    }
  }

  &__input-radio {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    flex-grow: 0;
    border: 1px solid #90a0b7;
    border-radius: 50%;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 50% 50%;

    &:not(:disabled):not(.default):hover {
      border: 1px solid #0489e0;
    }

    &.default {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }

  &__button-edit {
    display: block;
    background-image: url("@/assets/img/pen-grey.svg");
    background-size: contain;
    background-repeat: no-repeat;
    width: 14px;
    height: 14px;
    cursor: pointer;

    &:hover {
      background-image: url("@/assets/img/pen-blue.svg");
    }
  }

  &__button-delete {
    display: block;
    background-size: contain;
    background-repeat: no-repeat;
    width: 12px;
    height: 12px;
    cursor: pointer;
    background-image: url("@/assets/img/exit-grey.svg");
    &:hover {
      background-image: url("@/assets/img/exit-blue.svg");
    }
  }

  &__button-cash-out {
    @include buttons.btn-blue(50%, 48px, 16px);
    width: 100%;
    color: white !important;
    text-decoration: none;
  }

  &__form {
    display: grid;
    grid-gap: 34px;
    grid-template-columns: 1fr;
    padding-block: 20px;

    &__actions {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: 1fr 1fr;
      max-width: 438px;
      width: 100%;
      margin-left: auto;
    }

    &__cancel {
      @include buttons.btn-transparent(100%, 3rem, 1rem);
    }

    &__submit {
      @include buttons.btn-blue(100%, 3rem, 1rem);
    }
  }
}

@media screen and (max-width: 480px) {
  .cashbox-list {
    &__body {
      padding: 38px 10px 48px;
    }

    &__header {
      flex-direction: column;
    }

    &__table {
      &__head {
        display: none;
      }

      &__row {
        grid-gap: 10px;
        grid-template-columns: 1fr;
        margin: 0 0 20px 0;
      }

      &__cell {
        display: flex;
        justify-content: space-between;
        text-align: right;
        &:before {
          content: attr(data-label);
          font-weight: 700;
          margin-right: 20px;
        }
        &.action {
          justify-self: auto;
        }
      }
    }
  }
}
</style>
